// ============================================
// OPEN REGISTERS INDICATOR
// Shows count of open registers in header/dashboard
// ============================================

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CreditCard, AlertCircle, CheckCircle2 } from 'lucide-react';

const OpenRegistersIndicator = ({ className = '', showDetails = false }) => {
    const { api, user } = useAuth();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStatus = async () => {
            if (!user) return;
            
            try {
                const response = await api().get('/auto-closure/status');
                setStatus(response.data);
            } catch (error) {
                console.error('Error fetching register status:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStatus();
        
        // Refresh every 30 seconds
        const interval = setInterval(fetchStatus, 30000);
        return () => clearInterval(interval);
    }, [api, user]);

    if (loading || !status) {
        return null;
    }

    const { open_count, all_closed, unverified_auto_closures } = status;

    // Don't show if everything is fine
    if (all_closed && unverified_auto_closures === 0 && !showDetails) {
        return null;
    }

    return (
        <div className={`flex items-center gap-3 ${className}`}>
            {/* Open registers indicator */}
            {open_count > 0 ? (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 text-sm font-medium">
                    <CreditCard className="w-4 h-4" />
                    <span>{open_count} caisse{open_count > 1 ? 's' : ''} ouverte{open_count > 1 ? 's' : ''}</span>
                </div>
            ) : (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Toutes les caisses fermées</span>
                </div>
            )}

            {/* Unverified auto-closures alert (Admin only) */}
            {unverified_auto_closures > 0 && user?.role === 'ADMIN' && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/20 text-red-600 dark:text-red-400 text-sm font-medium animate-pulse">
                    <AlertCircle className="w-4 h-4" />
                    <span>{unverified_auto_closures} à vérifier</span>
                </div>
            )}

            {showDetails && status.open_registers?.length > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                    {status.open_registers.map((r, i) => r.register_name).join(', ')}
                </div>
            )}
        </div>
    );
};

export default OpenRegistersIndicator;
